/**
 * Hook поднимает WS-канал к /v2/notifications/ws и поддерживает store
 * актуальным через push (live) + polling fallback (30s).
 *
 * Подписка идёт лениво: hook вызывается только в `Layout`/`PageBar`
 * после авторизации (когда есть JWT в localStorage).
 */

import { useCallback, useEffect, useRef } from "react";

import { getApiBaseUrl } from "@/config/apiBaseUrl";
import { type NotificationItem } from "@/services/api";
import { useAppStore } from "@/store/appStore";
import { useInAppNotificationsStore } from "@/store/notificationsStore";

const POLL_INTERVAL_MS = 30_000;
const RECONNECT_BACKOFF_MS = [1_000, 3_000, 10_000, 30_000];

function buildWsUrl(token: string): string {
  const httpBase = getApiBaseUrl();
  const wsBase = httpBase.replace(/^http(s?):\/\//, (_, s) => (s ? "wss://" : "ws://"));
  const url = new URL(`${wsBase}/v2/notifications/ws`);
  url.searchParams.set("token", token);
  return url.toString();
}

function getStoredAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("access_token") || localStorage.getItem("auth_token") || null;
}

export function useInAppNotifications(): void {
  const reload = useInAppNotificationsStore((s) => s.reload);
  const refreshUnreadCount = useInAppNotificationsStore((s) => s.refreshUnreadCount);
  const applyIncoming = useInAppNotificationsStore((s) => s.applyIncoming);
  const addToast = useAppStore((s) => s.addNotification);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef<number>(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUnmountRef = useRef<boolean>(false);

  const handleIncomingNotification = useCallback(
    (item: NotificationItem) => {
      applyIncoming(item);
      // Дублируем кратким toast'ом для UX-feedback'а (без action, без дубля).
      addToast({
        type: item.severity === "critical" || item.severity === "warning" ? "warning" : "info",
        title: item.title,
        message: item.body ?? undefined,
        duration: 4000,
      });
    },
    [applyIncoming, addToast],
  );

  const connectWebSocket = useCallback(() => {
    const token = getStoredAccessToken();
    if (!token) return;

    try {
      const ws = new WebSocket(buildWsUrl(token));
      wsRef.current = ws;

      ws.addEventListener("open", () => {
        reconnectAttemptRef.current = 0;
      });

      ws.addEventListener("message", (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg?.type === "notification.created" && msg?.notification) {
            const n = msg.notification as NotificationItem & { is_read?: boolean };
            handleIncomingNotification({ ...n, is_read: n.is_read ?? false });
          }
        } catch (e) {
          console.warn("Failed to parse WS message:", e);
        }
      });

      ws.addEventListener("close", () => {
        if (closedByUnmountRef.current) return;
        const attempt = reconnectAttemptRef.current;
        const delay = RECONNECT_BACKOFF_MS[Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1)];
        reconnectAttemptRef.current = attempt + 1;
        reconnectTimerRef.current = setTimeout(connectWebSocket, delay);
      });

      ws.addEventListener("error", () => {
        // close handler сам инициирует переподключение.
      });
    } catch (e) {
      console.warn("Failed to open WS:", e);
    }
  }, [handleIncomingNotification]);

  // Первая загрузка + WS-подключение + polling fallback.
  useEffect(() => {
    closedByUnmountRef.current = false;
    void reload();
    connectWebSocket();

    const pollTimer = setInterval(() => {
      // Polling — лёгкий: только счётчик. Полная лента подгружается при
      // открытии панели (panel.useEffect → reload).
      void refreshUnreadCount();
    }, POLL_INTERVAL_MS);

    return () => {
      closedByUnmountRef.current = true;
      clearInterval(pollTimer);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [reload, refreshUnreadCount, connectWebSocket]);
}

export async function reloadNotifications(): Promise<void> {
  await useInAppNotificationsStore.getState().reload();
}
